import { useState } from 'react';

import { Card } from '@/components/ui/Card';
import { ScrollList } from '@/components/ui/ScrollList';
import { ErrorState, LoadingState } from '@/components/ui/StateMessage';
import { formatDate } from '@/utils/date';
import { formatMoney } from '@/utils/money';

import { useMemberInvoiceFeed } from '../../hooks/useInvoices';
import { InvoiceDialog } from '../InvoiceDialog';
import { InvoicePaymentBadge } from '../InvoicePaymentBadge';
import styles from './MemberInvoicesCard.module.css';

export interface MemberInvoicesCardProps {
  memberId: number;
}

/**
 * A member's bills, newest first. The list scrolls inside the card and loads more as it goes, so
 * a long-standing member never makes the profile long. Opening one shows its payments.
 */
export function MemberInvoicesCard({ memberId }: MemberInvoicesCardProps) {
  const [openId, setOpenId] = useState<number | null>(null);
  const invoices = useMemberInvoiceFeed(memberId);
  const rows = invoices.items;

  return (
    <Card
      title="Bills"
      description={invoices.total ? `${String(invoices.total)} in all` : undefined}
      padding="flush"
    >
      {invoices.isPending && <LoadingState compact />}
      {invoices.isError && (
        <ErrorState
          compact
          error={invoices.error}
          onRetry={() => {
            void invoices.refetch();
          }}
        />
      )}
      {rows &&
        (rows.length === 0 ? (
          // Not a blank card: memberships sold before billing existed genuinely have no bill.
          <p className={styles.empty}>
            No bills yet. Bills start from the first sale made after billing was added.
          </p>
        ) : (
          <ScrollList
            hasMore={invoices.hasMore}
            isLoadingMore={invoices.isLoadingMore}
            onLoadMore={invoices.loadMore}
          >
            <ul className={styles.list}>
              {rows.map((invoice) => (
                <li key={invoice.id} className={styles.item}>
                  <button
                    type="button"
                    className={styles.row}
                    onClick={() => {
                      setOpenId(invoice.id);
                    }}
                  >
                    <span className={styles.text}>
                      <span className={`${styles.number} tabular`}>{invoice.invoiceNumber}</span>
                      <span className={styles.muted}>
                        {invoice.description} · {formatDate(invoice.issueDate)}
                      </span>
                    </span>
                    <span className={styles.right}>
                      {/* Nothing is owed on a cancelled bill. */}
                      <span
                        className={`tabular ${
                          invoice.dueAmount === '0.00' || invoice.paymentState === 'cancelled'
                            ? styles.settled
                            : styles.due
                        }`}
                      >
                        {invoice.paymentState === 'cancelled'
                          ? '—'
                          : formatMoney(invoice.dueAmount)}
                      </span>
                      <InvoicePaymentBadge state={invoice.paymentState} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </ScrollList>
        ))}

      <InvoiceDialog
        invoiceId={openId}
        onClose={() => {
          setOpenId(null);
        }}
      />
    </Card>
  );
}
